import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Container,
  ErrorState,
  NoPermissionState,
  OrderStatusBadge,
  PageHeader,
  Skeleton,
  Table,
  TableWrap,
  Td,
  Th,
  TicketText,
  Tr,
} from '@horevia/ui';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { BedDouble, Printer, ReceiptText } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSession } from '@/features/auth/session';
import { openReceipt, openTransferSlip, orderQueryOptions } from '@/features/pos/queries';
import { itemTone, orderTone, preparationTone } from '@/features/pos/status';
import { errorMessage, errorTraceId } from '@/lib/errors';
import { formatAmount, formatDateTime } from '@/lib/format';

export const Route = createFileRoute('/_app/pos/orders/$orderId')({ component: OrderDetailPage });

/** Fiche d'une commande : lignes, taxes, paiements, tickets cuisine (texte figé). */
function OrderDetailPage() {
  const { t, i18n } = useTranslation(['pos', 'payments', 'common', 'errors']);
  const { orderId } = Route.useParams();
  const session = useSession();
  const canRead = session.can('order.read');
  const query = useQuery(orderQueryOptions(orderId, canRead));
  const [shownTicket, setShownTicket] = useState<string | null>(null);

  if (!canRead) {
    return (
      <Container>
        <NoPermissionState
          title={t('common:states.noPermissionTitle')}
          description={t('common:states.noPermissionDescription')}
        />
      </Container>
    );
  }
  if (query.isPending) {
    return (
      <Container>
        <div className="stack" aria-busy="true">
          <Skeleton variant="title" />
          <Skeleton height={240} />
        </div>
      </Container>
    );
  }
  if (query.isError || !query.data) {
    return (
      <Container>
        <ErrorState
          title={t('common:states.errorTitle')}
          description={errorMessage(query.error, t)}
          traceId={errorTraceId(query.error)}
          retryLabel={t('common:actions.retry')}
          onRetry={() => void query.refetch()}
          traceCopyLabel={t('common:actions.copy')}
          traceCopiedLabel={t('common:actions.copied')}
        />
      </Container>
    );
  }

  const order = query.data;
  const money = (value: number) => formatAmount(value, i18n.language, order.currencyCode);

  return (
    <Container>
      <PageHeader
        title={`${order.reference} · #${order.dailyNumber}`}
        subtitle={`${order.outletName} · ${order.tableCode ? `${t('pos:newOrder.table')} ${order.tableCode}` : order.tabName ? t('pos:ticket.tab', { name: order.tabName }) : t(`pos:serviceType.${order.serviceType}`)} · ${formatDateTime(order.openedAt, i18n.language)}`}
        breadcrumb={[
          { label: t('common:appName'), link: <Link to="/">{t('common:appName')}</Link> },
          {
            label: t('pos:orders.title'),
            link: <Link to="/pos/orders">{t('pos:orders.title')}</Link>,
          },
          { label: order.reference },
        ]}
        actions={
          <div className="row row--wrap" style={{ gap: 8 }}>
            <OrderStatusBadge tone={orderTone(order.status)}>
              {t(`pos:status.${order.status}`)}
            </OrderStatusBadge>
            <OrderStatusBadge tone={preparationTone(order.preparationStatus)}>
              {t(`pos:preparation.${order.preparationStatus}`)}
            </OrderStatusBadge>
            {order.status === 'TRANSFERRED' && order.stayReservationId ? (
              <>
                <Button variant="secondary" asChild>
                  <Link
                    to="/reservations/$reservationId"
                    params={{ reservationId: order.stayReservationId }}
                  >
                    <BedDouble className="ico" aria-hidden="true" />
                    {t('pos:orders.detail.stay', { reference: order.stayReference ?? '' })}
                  </Link>
                </Button>
                <Button variant="secondary" onClick={() => openTransferSlip(order.id)}>
                  <Printer className="ico" aria-hidden="true" />
                  {t('pos:ticket.transferSlip')}
                </Button>
              </>
            ) : (
              <Button variant="secondary" onClick={() => openReceipt(order.id)}>
                <Printer className="ico" aria-hidden="true" />
                {t('pos:ticket.receipt')}
              </Button>
            )}
            {order.status === 'OPEN' && session.can('order.create') ? (
              <Button variant="primary" asChild>
                <Link to="/pos" search={{ order: order.id }}>
                  <ReceiptText className="ico" aria-hidden="true" />
                  {t('pos:orders.detail.openInPos')}
                </Link>
              </Button>
            ) : null}
          </div>
        }
      />

      <div className="stack">
        <Card>
          <CardHeader title={t('pos:orders.detail.lines')} />
          <CardBody density="flush">
            <TableWrap>
              <Table>
                <thead>
                  <tr>
                    <Th>{t('pos:catalog.fields.name')}</Th>
                    <Th align="right">{t('pos:optionsDialog.quantity')}</Th>
                    <Th align="right">{t('pos:catalog.fields.price')}</Th>
                    <Th align="right">{t('pos:ticket.total')}</Th>
                    <Th>{t('pos:orders.columns.status')}</Th>
                  </tr>
                </thead>
                <tbody>
                  {order.items.map((item) => (
                    <Tr key={item.id}>
                      <Td>
                        {item.label}
                        {item.options.length > 0 || item.notes ? (
                          <span className="hint" style={{ display: 'block' }}>
                            {[
                              ...item.options.map((option) => option.optionName),
                              item.notes ? `« ${item.notes} »` : null,
                            ]
                              .filter(Boolean)
                              .join(' · ')}
                          </span>
                        ) : null}
                        {item.cancellationReason ? (
                          <span className="hint" style={{ display: 'block' }}>
                            {t('pos:orders.detail.cancellationReason')} : {item.cancellationReason}
                          </span>
                        ) : null}
                      </Td>
                      <Td numeric align="right">
                        {item.quantity}
                      </Td>
                      <Td numeric align="right">
                        {money(item.unitPrice)}
                      </Td>
                      <Td numeric align="right">
                        {money(item.amount)}
                      </Td>
                      <Td>
                        <OrderStatusBadge tone={itemTone(item.status)}>
                          {t(`pos:itemStatus.${item.status}`)}
                        </OrderStatusBadge>
                      </Td>
                    </Tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <Td colSpan={3} align="right">
                      {t('pos:ticket.subtotal')}
                    </Td>
                    <Td numeric align="right">
                      {money(order.subtotal)}
                    </Td>
                    <Td />
                  </tr>
                  {order.discountTotal > 0 ? (
                    <tr>
                      <Td colSpan={3} align="right">
                        {t('pos:ticket.discount')}
                        {order.discountReason ? ` — ${order.discountReason}` : ''}
                      </Td>
                      <Td numeric align="right">
                        − {money(order.discountTotal)}
                      </Td>
                      <Td />
                    </tr>
                  ) : null}
                  {order.taxes.map((tax) => (
                    <tr key={tax.code}>
                      <Td colSpan={3} align="right">
                        {tax.isInclusive ? `dont ${tax.label}` : tax.label}
                      </Td>
                      <Td numeric align="right">
                        {money(tax.amount)}
                      </Td>
                      <Td />
                    </tr>
                  ))}
                  <tr>
                    <Td colSpan={3} align="right" strong>
                      {t('pos:ticket.total')}
                    </Td>
                    <Td numeric align="right" strong>
                      {money(order.total)}
                    </Td>
                    <Td />
                  </tr>
                  <tr>
                    <Td colSpan={3} align="right">
                      {t('pos:ticket.paid')}
                    </Td>
                    <Td numeric align="right">
                      {money(order.paidTotal)}
                    </Td>
                    <Td />
                  </tr>
                </tfoot>
              </Table>
            </TableWrap>
            {order.cancellationReason ? (
              <p className="hint" style={{ padding: 16 }}>
                {t('pos:orders.detail.cancellationReason')} : {order.cancellationReason}
              </p>
            ) : null}
          </CardBody>
        </Card>

        <Card>
          <CardHeader title={t('pos:orders.detail.payments')} />
          <CardBody density="flush">
            {order.payments.length === 0 ? (
              <p className="hint" style={{ padding: 16 }}>
                {t('pos:orders.detail.noPayments')}
              </p>
            ) : (
              <TableWrap>
                <Table compact>
                  <tbody>
                    {order.payments.map((payment) => (
                      <Tr key={payment.id}>
                        <Td>
                          <Link
                            to="/billing/payments/$paymentId"
                            params={{ paymentId: payment.id }}
                            className="mono"
                          >
                            {payment.reference}
                          </Link>
                        </Td>
                        <Td>
                          {t(`payments:method.${payment.method}`, { defaultValue: payment.method })}
                        </Td>
                        <Td>{formatDateTime(payment.paidAt, i18n.language)}</Td>
                        <Td>
                          {payment.status === 'REVERSED'
                            ? t('pos:status.CANCELLED')
                            : (payment.recordedByName ?? '')}
                        </Td>
                        <Td numeric align="right">
                          {money(payment.amount)}
                        </Td>
                      </Tr>
                    ))}
                  </tbody>
                </Table>
              </TableWrap>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader title={t('pos:orders.detail.tickets')} />
          <CardBody>
            {order.tickets.length === 0 ? (
              <p className="hint">{t('pos:orders.detail.noTickets')}</p>
            ) : (
              <div className="stack">
                {order.tickets.map((ticket) => (
                  <div key={ticket.id} className="stack" style={{ gap: 6 }}>
                    <div className="row" style={{ gap: 8, alignItems: 'center' }}>
                      <span className="strong">
                        {ticket.kind === 'CANCEL'
                          ? t('pos:kitchen.cancelTicket')
                          : `${t('pos:kitchen.title')} ${ticket.sequence}`}
                      </span>
                      <span className="hint">
                        {ticket.destinationName ?? '—'} · {ticket.status} ·{' '}
                        {formatDateTime(ticket.createdAt, i18n.language)}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          setShownTicket((current) => (current === ticket.id ? null : ticket.id))
                        }
                      >
                        {t('pos:kitchen.showText')}
                      </Button>
                    </div>
                    {shownTicket === ticket.id ? <TicketText>{ticket.content}</TicketText> : null}
                  </div>
                ))}
              </div>
            )}
          </CardBody>
        </Card>
      </div>
    </Container>
  );
}
